import { useAppSelector } from "app/hooks";
import {
  SimulationFields,
  SimulationResponse,
} from "components/Layout/Campaign/CampaignSimulation/types";
import { formatSubmitValues } from "components/Layout/Campaign/Common/campaignHelpers";
import { RowsSelected } from "components/Table/useTableSelectable";
import { useAuth } from "hooks/auth";
import { InfiniteResponse } from "hooks/useManageInfiniteScroll";
import { querystring } from "models/Listing";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import useSWR, { SWRResponse } from "swr";
import useSWRInfinite from "swr/infinite";
import { AddCampaignBatchResponse, CampaignBEData } from "types/campaigns";
import { ListingProps } from "types/listing";
import { getEnvVariable } from "utilities/env";

export function useCampaigns() {
  const { code } = useAppSelector(selectCurrentStoreData);
  const { fetcher } = useAuth();

  const useCampaignsList = (props?: ListingProps) => {
    return useSWR(
      () =>
        code
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${code}/campaign${querystring(props)}`
          : null,
      fetcher,
    );
  };

  const useInfiniteCampaignsList = (props?: ListingProps) => {
    return useSWRInfinite<InfiniteResponse<CampaignBEData>>(
      (index) =>
        code
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${code}/campaign${querystring({ ...props, page: index + 1 })}`
          : null,
      fetcher,
      { revalidateFirstPage: false },
    );
  };

  const addCampaignBatch = async (values: any[]) => {
    if (!code) return;

    const responses: AddCampaignBatchResponse = await Promise.all(
      values.map(({ campaignId, name, ...rest }) =>
        campaignId
          ? new Promise((resolve) =>
              resolve({
                campaignId,
                name,
                ...rest,
              }),
            )
          : fetcher(`${getEnvVariable("REACT_APP_API_URL")}/${code}/campaign`, {
              body: JSON.stringify({
                campaign: formatSubmitValues({ values: rest }),
              }),
              headers: { "Content-Type": "application/json" },
              method: "post",
            }),
      ),
    )
      .then((results) =>
        Promise.all(results.map((r) => (r.json ? r.json() : r))),
      )
      .then((resp) =>
        resp.map((r, i) => ({
          ...r,
          data: values[i],
        })),
      );
    return responses;
  };

  const addCampaign = async (values: any | any[]) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${code}/campaign`,
      {
        body: JSON.stringify({ campaign: values }),
        headers: { "Content-Type": "application/json" },
        method: "post",
      },
    );

    const data = await response.json();
    if (response.ok) {
      return { data };
    } else {
      throw data;
    }
  };

  const editCampaign = async (campaignID: string, values: any) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${code}/campaign/${campaignID}`,
      {
        body: JSON.stringify({ campaign: values }),
        headers: { "Content-Type": "application/json" },
        method: "put",
      },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };

  const updateCampaign = async (campaignID: string, value: object) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${code}/campaign/${campaignID}`,
      {
        body: JSON.stringify({ campaign: value }),
        headers: { "Content-Type": "application/json" },
        method: "PATCH",
      },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };

  const deleteCampaign = async (campaignId: string) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${code}/campaign/${campaignId}`,
      { method: "delete" },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };

  const deleteCampaignBatch = async (selected: RowsSelected) => {
    const campaignIds = selected.map(({ id }) => id);

    const response = await Promise.all(
      campaignIds.map((campaignId) =>
        fetcher(
          `${getEnvVariable("REACT_APP_API_URL")}/${code}/campaign/${campaignId}`,
          { method: "delete" },
        ),
      ),
    ).then(async (resp) => {
      return Promise.all(
        resp.map(async (r, i) => {
          return {
            error: r.status !== 204 ? await r?.json() : null,
            campaignName: selected[i].data.name,
          };
        }),
      );
    });

    return response.filter((campaign) => campaign.error);
  };

  const useCampaignData = (
    campaignID: string,
  ): SWRResponse<CampaignBEData, any> => {
    return useSWR(
      () =>
        code && campaignID
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${code}/campaign/${campaignID}`
          : null,
      fetcher,
    );
  };

  const simulateCampaign = async (values: SimulationFields) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${code}/campaign/simulate`,
      {
        body: JSON.stringify({ simulate: values }),
        headers: { "Content-Type": "application/json" },
        method: "post",
      },
    );
    const data: SimulationResponse = await response.json();
    if (response.ok) {
      return { data };
    } else {
      throw data;
    }
  };

  const useCampaignRedemptionCodeList = (
    campaignId?: string,
    props?: ListingProps,
  ) => {
    return useSWR(
      () =>
        code && campaignId
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${code}/campaign/${campaignId}/codes${querystring(props)}`
          : null,
      fetcher,
    );
  };

  const generateRedemptionCodes = async (
    numberOfCodes?: number,
    campaignId?: string,
  ) => {
    const response = await fetcher(
      `${getEnvVariable(
        "REACT_APP_API_URL",
      )}/${code}/campaign/${campaignId}/codes/generate`,
      {
        body: JSON.stringify({ generate: { numberOfCodes: numberOfCodes } }),
        headers: { "Content-Type": "application/json" },
        method: "post",
      },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };

  const useCampaignOverviewAnalytics = (
    campaignId: string | undefined,
    props?: ListingProps,
  ) => {
    return useSWR(
      () =>
        code && campaignId
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${code}/analytics/campaign/single-view/${campaignId}${querystring(
              props,
            )}`
          : null,
      fetcher,
    );
  };

  return {
    addCampaign,
    editCampaign,
    updateCampaign,
    deleteCampaign,
    simulateCampaign,
    useCampaignData,
    useCampaignsList,
    useCampaignOverviewAnalytics,
    useCampaignRedemptionCodeList,
    generateRedemptionCodes,
    addCampaignBatch,
    useInfiniteCampaignsList,
    deleteCampaignBatch,
  };
}
